import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { toast } from '@/hooks/use-toast';

export interface SavedSearchRow {
  id: string;
  name: string;
  query_string: string;
  created_at: string;
}

/** Gemte søgninger — hele URL-querystringen gemmes, så filtrene kan genskabes 1:1. */
export function useSavedSearches() {
  const { user } = useAuth();
  const userId = user?.id ?? null;
  const qc = useQueryClient();

  const query = useQuery({
    queryKey: ['saved-searches', userId],
    enabled: !!userId,
    staleTime: 60 * 1000,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('saved_searches')
        .select('id, name, query_string, created_at')
        .order('created_at', { ascending: false });
      if (error) throw new Error(error.message);
      return (data || []) as SavedSearchRow[];
    },
  });

  const save = useMutation({
    mutationFn: async ({ name, queryString }: { name: string; queryString: string }) => {
      if (!userId) throw new Error('login');
      const { error } = await supabase
        .from('saved_searches')
        .insert({ user_id: userId, name, query_string: queryString });
      if (error) throw new Error(error.message);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['saved-searches', userId] });
      toast({ title: 'Søgning gemt' });
    },
    onError: (err: Error) => {
      toast({
        title: err.message === 'login' ? 'Log ind for at gemme søgninger' : 'Kunne ikke gemme søgningen',
        variant: 'destructive',
      });
    },
  });

  const remove = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('saved_searches').delete().eq('id', id);
      if (error) throw new Error(error.message);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['saved-searches', userId] });
      toast({ title: 'Søgning slettet' });
    },
  });

  return {
    searches: query.data || [],
    isLoading: query.isLoading,
    isLoggedIn: !!userId,
    saveSearch: save.mutate,
    isSaving: save.isPending,
    removeSearch: remove.mutate,
  };
}
